import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { BottomNav } from './components/BottomNav';
import { OfflineBanner } from './components/OfflineBanner';
import { SyncStatusBadge } from './components/SyncStatusBadge';
import { startSyncEngine } from './services/offlineSync';
import { purgeOldEntries } from './services/offlineDB';
import { LoadingScreen } from './components/LoadingScreen';
import { DispatchNotification } from './components/DispatchNotification';
import { useDispatchNotifications } from './hooks/useDispatchNotifications';
import { LocationProvider } from './contexts/LocationContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import RouteSheetPage from './pages/RouteSheetPage';
import InspectionPage from './pages/InspectionPage';
import ServiceHoursPage from './pages/ServiceHoursPage';
import MessagesPage from './pages/MessagesPage';
import ProfilePage from './pages/ProfilePage';
import { FreeTierProvider } from './components/FreeTierProvider';
import DeleteAccountPage from './pages/DeleteAccountPage';
import VehiclePage from './pages/VehiclePage';
import NotificationsPage from './pages/NotificationsPage';
import SecurityPage from './pages/SecurityPage';
import HelpPage from './pages/HelpPage';
import LegalPage from './pages/LegalPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import SupportPage from './pages/SupportPage';
import DocumentsOnboardPage from './pages/DocumentsOnboardPage';
import ReportsListPage from './pages/ReportsListPage';
import DailyReportPage from './pages/DailyReportPage';
import DocumentViewerPage from './pages/DocumentViewerPage';
import EnterprisesPage from './pages/EnterprisesPage';
import DriverOrdersPage from './pages/DriverOrdersPage';
import DriverExpensesPage from './pages/DriverExpensesPage';
import AdminRoutes from './admin/AdminRoutes';
// Client Portal Pages
import ClientLoginPage from './pages/client-portal/ClientLoginPage';
import AcceptInvitePage from './pages/client-portal/AcceptInvitePage';
import ClientDashboard from './pages/client-portal/ClientDashboard';
import PresentationPage from './pages/PresentationPage';
import AccueilPage from './pages/accueil/AccueilPage';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Apple Guideline 3.1.1 (steering) + cohérence B2B :
// La route publique /presentation qui liste les forfaits payants est bloquée
// sur toutes les apps natives (iOS + Android). Elle reste accessible uniquement
// sur le Web (navigateur classique), où les prospects peuvent découvrir l'offre.
const IS_NATIVE = Capacitor.isNativePlatform();

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Layout with bottom navigation
const AppLayout = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { isAuthenticated, token } = useAuth();
  const location = useLocation();
  const dispatch = useDispatchNotifications();
  
  // Don't show BottomNav on admin, client-portal, mechanic app, or public marketing routes
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isClientPortalRoute = location.pathname.startsWith('/client-portal');
  const isMechanicRoute = location.pathname.startsWith('/atelier');
  const isPublicMarketingRoute = ['/presentation', '/presentation/diaporama', '/privacy', '/terms', '/delete-account'].includes(location.pathname);
  const showBottomNav = isAuthenticated && !isAdminRoute && !isClientPortalRoute && !isMechanicRoute && !isPublicMarketingRoute;

  // GPS tracking is mounted ONCE at the app layout level so it persists
  // across page navigations (Dashboard → Reports → Inspections → etc.).
  // Auto-start happens inside the hook when isAuthenticated becomes true.
  // We only enable tracking on driver routes (not admin/atelier/client/public).
  const shouldTrackGps = isAuthenticated && !isAdminRoute && !isClientPortalRoute && !isMechanicRoute && !isPublicMarketingRoute;

  useEffect(() => {
    if (!isAuthenticated || !token || isAdminRoute || isClientPortalRoute || isMechanicRoute || isPublicMarketingRoute) return;

    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadCount(response.data.count || 0);
      } catch (err) {
        // Silently ignore errors for unread count
        setUnreadCount(0);
      }
    };

    // Delay initial fetch to ensure token is set
    const timeout = setTimeout(fetchUnreadCount, 500);
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => {
      clearTimeout(timeout);
      clearInterval(timeout);
      clearInterval(interval);
    };
  }, [isAuthenticated, token, isAdminRoute, isClientPortalRoute, isMechanicRoute, isPublicMarketingRoute]);

  return (
    <LocationProvider enabled={shouldTrackGps}>
      <div className="app-container">
        {!isAdminRoute && !isClientPortalRoute && !isMechanicRoute && !isPublicMarketingRoute && <OfflineBanner />}
        {/* Dispatch notifications for drivers */}
        {showBottomNav && (
          <DispatchNotification
            newStopsCount={dispatch.newStopsCount}
            notificationType={dispatch.notificationType}
            showNotification={dispatch.showNotification}
            dismissPopup={dispatch.dismissPopup}
          />
        )}
        {children}
        {showBottomNav && !isMechanicRoute && (
          <BottomNav
            unreadCount={unreadCount}
            newDispatchCount={dispatch.newStopsCount}
          />
        )}
      </div>
    </LocationProvider>
  );
};

// Main App Routes
const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <FreeTierProvider>

    <Routes>
      {/* /presentation exposée uniquement sur le Web — cachée sur iOS + Android */}
      {!IS_NATIVE && <Route path="/presentation" element={<AccueilPage />} />}
      {!IS_NATIVE && <Route path="/presentation/diaporama" element={<PresentationPage />} />}
      {IS_NATIVE && <Route path="/presentation" element={<Navigate to="/login" replace />} />}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route 
        path="/login" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
        } 
      />
      <Route 
        path="/register" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/route" 
        element={
          <ProtectedRoute>
            <RouteSheetPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/inspection" 
        element={
          <ProtectedRoute>
            <InspectionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/hours" 
        element={
          <ProtectedRoute>
            <ServiceHoursPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/messages" 
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } 
      />
      <Route path="/account/delete" element={<ProtectedRoute><DeleteAccountPage /></ProtectedRoute>} />
      <Route 
        path="/vehicle" 
        element={
          <ProtectedRoute>
            <VehiclePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/notifications" 
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/security" 
        element={
          <ProtectedRoute>
            <SecurityPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/help" 
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/legal" 
        element={
          <ProtectedRoute>
            <LegalPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/documents" 
        element={
          <ProtectedRoute>
            <DocumentsOnboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/driver/enterprises" 
        element={
          <ProtectedRoute>
            <EnterprisesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute>
            <ReportsListPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/orders" 
        element={
          <ProtectedRoute>
            <DriverOrdersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/expenses" 
        element={
          <ProtectedRoute>
            <DriverExpensesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports/new" 
        element={
          <ProtectedRoute>
            <DailyReportPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports/:reportId" 
        element={
          <ProtectedRoute>
            <DailyReportPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports/:reportId/stops/:stopId/documents" 
        element={
          <ProtectedRoute>
            <DocumentViewerPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/" 
        element={<Navigate to={isAuthenticated ? "/dashboard" : (window.__IS_NATIVE__ ? "/login" : "/presentation")} replace />} 
      />
      
      {/* Admin Routes - separate from driver app */}
      <Route path="/admin/*" element={<AdminRoutes />} />
      
      {/* Mechanic App - standalone */}
      <Route path="/atelier/login" element={<Navigate to="/admin/login" replace />} />
      <Route path="/atelier/*" element={<Navigate to="/admin/mecanicien" replace />} />
      
      {/* Client Portal Routes */}
      <Route path="/client-portal/login" element={<ClientLoginPage />} />
      <Route path="/client-portal/accept-invite/:token" element={<AcceptInvitePage />} />
      <Route path="/client-portal/dashboard" element={<ClientDashboard />} />
      
      <Route 
        path="*" 
        element={<Navigate to={isAuthenticated ? "/dashboard" : (window.__IS_NATIVE__ ? "/login" : "/presentation")} replace />} 
      />
    </Routes>
</FreeTierProvider>  );
};

function App() {
  useEffect(() => {
    startSyncEngine();
    // Purge asynchrone du storage IndexedDB obsolète — critique pour éviter
    // le "démarrage app lent" (blobs BOL scans uploadés + mutations terminées
    // qui gonflaient indéfiniment). Non-bloquant : différé après le mount.
    setTimeout(() => { purgeOldEntries().catch(() => {}); }, 3000);
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppLayout>
          <AppRoutes />
          <SyncStatusBadge />
        </AppLayout>
        <Toaster 
          position="top-center" 
          toastOptions={{
            style: {
              background: 'hsl(222, 47%, 7%)',
              border: '1px solid hsl(217, 33%, 17%)',
              color: 'white'
            }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
